import { useState } from 'react'
import dayjs from 'dayjs'
import { Alert, Button, DatePicker, Dropdown, Form, InputNumber, Modal, Select } from 'antd'
import { MoreOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { DataTable } from '@kolab/dms-base/components/ui'
import { PageLayout } from '@kolab/dms-base/components/layout'
import { toast, getFriendlyErrorMessage } from '@kolab/dms-base/lib'
import {
  issueDistributorLicense,
  listDistributorLicenses,
  setDistributorLicenseStatus,
  updateDistributorLicense,
} from '../api/distributorLicense.api'
import { pickDistributors, pickLicenseProducts } from '../api/lookups.api'
import { distributorLicenseKeys } from '../api/queryKeys'
import type {
  DistributorLicense,
  DistributorLicenseStatus,
  LicenseType,
} from '../api/types'
import { StatusBadge } from '../components/common/StatusBadge'
import { Can, P, usePermissionStore } from '../rbac'
import { useCursorList } from '../hooks/useCursorList'
import { CursorListStateView } from '../components/common/QueryStateView'


type Mode =
  | { kind: 'closed' }
  | { kind: 'issue' }
  | { kind: 'edit'; license: DistributorLicense }

export default function DistributorLicensesPage() {
  const [distributorFilter, setDistributorFilter] = useState<string | undefined>()
  const queryClient = useQueryClient()

  const { items, isLoading, isError, error, refetch, isFetchingNextPage, hasNextPage, fetchNextPage } = useCursorList<DistributorLicense>({
    queryKey: distributorLicenseKeys.list({ pageSize: 20, distributorId: distributorFilter }),
    queryFn: (params) => listDistributorLicenses(params),
    params: { distributorId: distributorFilter },
    pageSize: 20,
  })

  const { hasPermission } = usePermissionStore()

  const distributorsQuery = useQuery({
    queryKey: ['lookups', 'distributors'],
    queryFn: () => pickDistributors(),
    enabled: hasPermission(P.tenants.distributor.pick),
  })

  const productsQuery = useQuery({
    queryKey: ['lookups', 'license-products'],
    queryFn: () => pickLicenseProducts(),
    enabled: hasPermission(P.license_catalog.product.pick),
  })

  const [modal, setModal] = useState<Mode>({ kind: 'closed' })
  const [formError, setFormError] = useState<string | null>(null)

  function handleFormError(error: unknown, fallback: string) {
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status?: number }).status
      if (status === 401 || status === 403 || (status && status >= 500)) return
    }
    setFormError(getFriendlyErrorMessage(error, fallback))
  }

  function openModal(mode: Mode) {
    setFormError(null)
    setModal(mode)
  }

  const issueMut = useMutation({
    mutationFn: issueDistributorLicense,
    onSuccess: () => {
      toast.success('License issued')
      queryClient.invalidateQueries({ queryKey: distributorLicenseKeys.all })
      setModal({ kind: 'closed' })
    },
    onError: (error: unknown) => handleFormError(error, 'Failed to issue license'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, totalDeviceLimit, expiryDate }: { id: string; totalDeviceLimit?: number; expiryDate?: string }) =>
      updateDistributorLicense(id, { totalDeviceLimit, expiryDate }),
    onSuccess: () => {
      toast.success('License updated')
      queryClient.invalidateQueries({ queryKey: distributorLicenseKeys.all })
      setModal({ kind: 'closed' })
    },
    onError: (error: unknown) => handleFormError(error, 'Failed to update license'),
  })

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: DistributorLicenseStatus }) =>
      setDistributorLicenseStatus(id, { status }),
    onSuccess: () => {
      toast.success('Status updated')
      queryClient.invalidateQueries({ queryKey: distributorLicenseKeys.all })
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status?: number }).status
        if (status === 401 || status === 403 || (status && status >= 500)) return
      }
      toast.error(getFriendlyErrorMessage(error, 'Failed to update license status'))
    },
  })

  const distributorOptions = (distributorsQuery.data?.items ?? []).map((d) => ({ label: d.label, value: d.id }))
  const productOptions = (productsQuery.data?.items ?? []).map((p) => ({ label: p.label, value: p.id }))

  const columns: ColumnsType<DistributorLicense> = [
    { title: 'License key', dataIndex: 'licenseKey', key: 'licenseKey' },
    {
      title: 'Distributor',
      dataIndex: 'distributorName',
      key: 'distributorName',
    },
    {
      title: 'Product',
      dataIndex: 'licenseProductName',
      key: 'licenseProductName',
    },
    { title: 'Type', dataIndex: 'licenseType', key: 'licenseType' },
    { title: 'Limit', dataIndex: 'totalDeviceLimit', key: 'totalDeviceLimit', align: 'right' as const },
    { title: 'Allocated', dataIndex: 'allocatedDeviceLimit', key: 'allocatedDeviceLimit', align: 'right' as const },
    { title: 'Available', dataIndex: 'availableDeviceLimit', key: 'availableDeviceLimit', align: 'right' as const },
    { title: 'Expiry', dataIndex: 'expiryDate', key: 'expiryDate' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <StatusBadge status={s} />,
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) => {
        const items: { key: string; label: string; onClick: () => void; danger?: boolean }[] = []
        if (hasPermission(P.licenses.distributor_pool.extend)) {
          items.push({
            key: 'edit',
            label: 'Extend / grow',
            onClick: () => openModal({ kind: 'edit', license: record }),
          })
        }
        if (hasPermission(P.licenses.distributor_pool.suspend)) {
          for (const status of ['DRAFT', 'ACTIVE', 'EXPIRED', 'SUSPENDED'] as DistributorLicenseStatus[]) {
            items.push({
              key: status,
              label: `Set ${status}`,
              onClick: () => statusMut.mutate({ id: record.id, status }),
            })
          }
        }
        if (hasPermission(P.licenses.distributor_pool.cancel)) {
          items.push({
            key: 'CANCELLED',
            label: 'Set CANCELLED',
            danger: true,
            onClick: () => statusMut.mutate({ id: record.id, status: 'CANCELLED' }),
          })
        }

        if (items.length === 0) return null
        return (
          <Dropdown trigger={['click']} menu={{ items }} getPopupContainer={() => document.body}>
            <Button size="small" icon={<MoreOutlined />} />
          </Dropdown>
        )
      },
    },
  ]

  return (
    <PageLayout
      actions={
        <Can permission={P.licenses.distributor_pool.create}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal({ kind: 'issue' })}>
            Issue license
          </Button>
        </Can>
      }
      filters={
        <Select
          allowClear
          placeholder="Filter by distributor"
          style={{ minWidth: 220 }}
          options={distributorOptions}
          value={distributorFilter}
          onChange={(v) => setDistributorFilter(v)}
        />
      }
    >
      <CursorListStateView isLoading={isLoading} isError={isError} error={error} refetch={refetch}>
        <DataTable<DistributorLicense>
          rowKey="id"
          columns={columns}
          dataSource={items}
          pagination={false}
          infiniteScroll={{ isFetchingNextPage, hasNextPage, fetchNextPage }}
        />
      </CursorListStateView>

      <IssueLicenseModal
        mode={modal}
        distributorOptions={distributorOptions}
        productOptions={productOptions}
        onCancel={() => setModal({ kind: 'closed' })}
        onIssue={(fields) => issueMut.mutate(fields)}
        onUpdate={(id, fields) => updateMut.mutate({ id, ...fields })}
        submitting={issueMut.isPending || updateMut.isPending}
        error={formError}
        onClearError={() => setFormError(null)}
      />
    </PageLayout>
  )
}

interface IssueFields {
  distributorId: string
  licenseProductId: string
  licenseType: LicenseType
  totalDeviceLimit: number
  expiryDate: string
  expiryDurationDays?: number
}

function IssueLicenseModal({
  mode,
  distributorOptions,
  productOptions,
  onCancel,
  onIssue,
  onUpdate,
  submitting,
  error,
  onClearError,
}: {
  mode: Mode
  distributorOptions: { label: string; value: string }[]
  productOptions: { label: string; value: string }[]
  onCancel: () => void
  onIssue: (fields: IssueFields) => void
  onUpdate: (id: string, fields: { totalDeviceLimit?: number; expiryDate?: string }) => void
  submitting: boolean
  error: string | null
  onClearError: () => void
}) {
  const [form] = Form.useForm<IssueFields>()
  const isEdit = mode.kind === 'edit'
  const licenseType = Form.useWatch('licenseType', form) as LicenseType | undefined

  const initial: Partial<IssueFields> = isEdit
    ? {
      totalDeviceLimit: mode.license.totalDeviceLimit,
      expiryDate: mode.license.expiryDate,
    }
    : {
      licenseType: 'CONCURRENT',
      totalDeviceLimit: 100,
    }

  return (
    <Modal
      open={mode.kind !== 'closed'}
      title={isEdit ? 'Extend / grow license' : 'Issue distributor license'}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okButtonProps={{ loading: submitting }}
      destroyOnClose
      width={520}
    >
      {error && (
        <Alert type="error" message={error} closable onClose={onClearError} style={{ marginBottom: 16 }} />
      )}
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={initial}
        onFinish={(values) => {
          const expiryDate = values.expiryDate
            ? dayjs.isDayjs(values.expiryDate) ? (values.expiryDate as dayjs.Dayjs).format('YYYY-MM-DD') : values.expiryDate
            : ''
          if (isEdit && mode.kind === 'edit') {
            onUpdate(mode.license.id, {
              totalDeviceLimit: values.totalDeviceLimit ?? 0,
              expiryDate,
            })
          } else {
            onIssue({ ...values, expiryDate })
          }
        }}
      >
        {!isEdit && (
          <>
            <Form.Item name="distributorId" label="Distributor" rules={[{ required: true }]}>
              <Select options={distributorOptions} placeholder="Select a distributor" />
            </Form.Item>
            <Form.Item name="licenseProductId" label="License product" rules={[{ required: true }]}>
              <Select options={productOptions} placeholder="Select an SKU" />
            </Form.Item>
            <Form.Item name="licenseType" label="Type" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'CONCURRENT', label: 'CONCURRENT (seat-based)' },
                  { value: 'CONSUMPTION', label: 'CONSUMPTION (burn-based)' },
                ]}
              />
            </Form.Item>
          </>
        )}
        <Form.Item
          name="totalDeviceLimit"
          label={isEdit ? 'New total device limit (0 to keep unchanged)' : 'Total device limit'}
          rules={isEdit ? [] : [{ required: true }]}
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name="expiryDate"
          label={isEdit ? 'New expiry date (empty to keep unchanged)' : 'Expiry date'}
          rules={isEdit ? [] : [{ required: true, message: 'Expiry date is required' }]}
        >
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
        {!isEdit && licenseType === 'CONSUMPTION' && (
          <Form.Item
            name="expiryDurationDays"
            label="Per-device expiry duration (days)"
            rules={[{ required: true, type: 'number', min: 1 }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}

export { DistributorLicensesPage }
